import type { Media } from '@/types';
import { BASE_URL, MEDIA_URL } from '@/constants/config';

// ─── Base URLs ──────────────────────────────────────────────────────────────
//
// The GraphQL API (promostork_backend) and the media server
// (promostork_media_server) run on DIFFERENT hosts/ports:
//   - API:   VITE_BASE_URL  (dev: http://localhost:4000)
//   - Media: VITE_MEDIA_URL (dev: http://localhost:8090)
//
// The backend hands clients ABSOLUTE media-server URLs (it prefixes
// MEDIA_PUBLIC_BASE_URL). When the stored host is a localhost placeholder we
// still rewrite it — a media URL must be rewritten to the MEDIA base, NOT the
// API base.

const API_BASE_URL = BASE_URL;
const MEDIA_BASE_URL = MEDIA_URL;

const LOCALHOST_RE = /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:\d+)?/i;
// A media-server delivery path looks like /public/img/..., /private/img/...,
// /public/video/..., /private/video/...
const MEDIA_PATH_RE = /\/(public|private)\/(img|video)\//;

function isMediaPath(url: string): boolean {
  return MEDIA_PATH_RE.test(url);
}

// ─── Image transforms (imgproxy via the media server) ────────────────────────
//
// Public images are served at `/public/img/<params>/<key>` where `<params>` is
// a comma-separated transform DSL the media server forwards to imgproxy:
//   w_<int>, h_<int>, fit_<cover|contain|fill|inside>, q_<1..100>, fmt_<auto|webp|avif|jpeg|png>
// The backend stores them with `orig` (pass-through). We rewrite that segment
// to request exactly the resolution a given surface needs instead of
// downloading full-res originals.
//
// Private (signed) URLs bake the transforms into the HMAC signature, so we
// must NOT rewrite them — we leave those untouched.

export interface ImageTransform {
  /**
   * Width in image pixels. Never multiplied by the screen's pixel ratio: the
   * URL must be the same in every browser and in a server render (hydration
   * keeps the server's `src`, and heads carry it). Size the presets for the
   * densest screen they serve, or give the image a `srcset`.
   */
  width?: number;
  /** Height in image pixels (see `width`). */
  height?: number;
  fit?: 'cover' | 'contain' | 'fill' | 'inside';
  /** JPEG/WebP quality 1–100. Defaults to 80. */
  quality?: number;
  /** Output format. `auto` (default) lets imgproxy negotiate WebP/AVIF from Accept. */
  format?: 'auto' | 'webp' | 'avif' | 'jpeg' | 'png';
}

// Ready-made presets so call sites stay declarative. Widths are sensible fixed
// values for web surfaces (mobile derived them from the screen width).
//
// Every preset is a plain value and nothing here reads `window` (nor the
// pixel ratio), so a URL built from a preset is the same in every browser and
// in a server render. The small presets are already at least 2x their boxes
// (a 200 px logo in a 72–96 px box).
export const ImagePresets = {
  /** Small logos / avatars rendered in a contained box. */
  thumb: { width: 120, fit: 'contain', quality: 80 } as ImageTransform,
  /** Logo inside a grid card. */
  logo: { width: 200, fit: 'contain', quality: 82 } as ImageTransform,
  /** Cover image filling a grid card. */
  card: { width: 480, fit: 'cover', quality: 80 } as ImageTransform,
  /**
   * Full-bleed hero / carousel / detail cover. Render it through `<Img sizes>`
   * so the browser picks a width from SRCSET_WIDTHS; the fixed width here is
   * only the single-`src` fallback, at 1x.
   *
   * It used to be a flat `width: 1280` at 2x the pixel ratio — 2560px for
   * every visitor, phone included. Measured against a real 1080x1080 source on the
   * media server:
   *
   *   orig                  59.9 KB
   *   w_2560 (old hero)     72.5 KB   <- 21% BIGGER than the original
   *   w_800  (right-sized)  54.0 KB   <- 26% smaller than the old hero
   *
   * imgproxy never upscales, so asking for more than the source has returns
   * the same pixels and only pays a re-encode penalty.
   */
  hero: { width: 1280, fit: 'cover', quality: 82 } as ImageTransform,
  /** High-quality full image for flyer pages and the fullscreen viewer (see `hero`). */
  full: { width: 1280, fit: 'inside', quality: 90 } as ImageTransform,
  /**
   * Open Graph / Twitter social card — fixed 1200×630 at 1x. JPEG, not `auto`:
   * the crawlers that fetch it (WhatsApp, Facebook, X) do not all send an
   * Accept header that negotiates to WebP/AVIF, and some reject those formats.
   */
  og: {
    width: 1200,
    height: 630,
    fit: 'cover',
    quality: 80,
    format: 'jpeg',
  } as ImageTransform,
} as const;

// ─── Responsive images (srcset ladder) ────────────────────────────────────────
//
// Fluid images (home hero, detail cover, flyer pages) are rendered with a
// `srcset` over ONE fixed width ladder plus a `sizes` hint, and the browser
// picks the candidate for its viewport and pixel density. That replaces sizing
// a single `src` from `window.innerWidth × devicePixelRatio`:
//   - the markup is identical for every visitor, so a server render can emit it
//     and hydration matches;
//   - the CDN caches at most four variants of an image instead of one per
//     device width;
//   - a 380 px phone slot no longer downloads a 2560 px cover.

/** Candidate widths (px) for every responsive image. Multiples of 32 (see `bucket`). */
export const SRCSET_WIDTHS = [320, 640, 960, 1280] as const;

/** `sizes` for an image spanning the `max-w-7xl px-4` content column (1280 px − 2 × 16 px). */
export const CONTENT_COLUMN_SIZES = '(min-width: 1280px) 1248px, calc(100vw - 32px)';

/**
 * Candidate widths for small fixed-size images — card logos in 72–96 px
 * boxes, list-row thumbnails, avatars — covering 1x to 3x of those boxes
 * without jumping to SRCSET_WIDTHS' 320 px step. Multiples of 32.
 */
export const THUMB_SRCSET_WIDTHS = [64, 128, 192, 288] as const;

/**
 * `sizes` for the logo box of a full Brand / Shop / Store card: the card's
 * grid column minus the box's 1 px borders and 2 × 16 px padding. The catalog,
 * search and favorites grids run 2 / 3 / 4 / 5 columns (base / md / lg / xl,
 * 16 px gaps); a store page's brand card sits in a 3-column grid beside the
 * 340 px sidebar, which is the widest box at xl (248 px). At md that store-page
 * card (2 columns there) is wider than this says; the lists win the trade.
 */
export const CARD_LOGO_SIZES =
  '(min-width: 1280px) 248px, (min-width: 1024px) calc(25vw - 54px), (min-width: 768px) calc(34vw - 55px), calc(50vw - 58px)';

export interface ResponsiveImageSource {
  src: string;
  /** Absent when the URL cannot be transformed (private, video, legacy, data:). */
  srcSet?: string;
}

const TRANSFORMABLE_RE = /\/public\/img\/[^/]+\//;

/**
 * `src` + `srcset` for the first URL of `media`, over SRCSET_WIDTHS.
 *
 * `transform` supplies fit/quality/format (and the aspect ratio when it sets
 * both width and height); its `width` only chooses which candidate doubles as
 * the plain `src` fallback. When the media's intrinsic width is known, the
 * ladder stops at the first step that covers it and labels that candidate with
 * the real width — imgproxy would return the source pixels for any larger
 * request anyway, and an honest descriptor keeps the browser from thinking it
 * got a sharper image than it did.
 */
export function responsiveImageSource(
  media?: Media | null,
  transform: ImageTransform = {},
  widths: readonly number[] = SRCSET_WIDTHS,
): ResponsiveImageSource | null {
  const raw = firstUrl(media);
  const base = getFullUrl(raw);
  if (!base) return null;
  if (!TRANSFORMABLE_RE.test(base) || widths.length === 0) return { src: base };

  const intrinsic =
    typeof media?.width === 'number' && media.width > 0 ? media.width : undefined;
  const candidates: Array<{ url: string; step: number; descriptor: number }> = [];
  for (const step of widths) {
    const height =
      transform.width && transform.height
        ? Math.round((transform.height * step) / transform.width)
        : undefined;
    const url = applyTransform(base, { ...transform, width: step, height });
    if (intrinsic !== undefined && step >= intrinsic) {
      candidates.push({ url, step, descriptor: intrinsic });
      break;
    }
    candidates.push({ url, step, descriptor: step });
  }

  const target = transform.width ?? widths[0];
  const fallback =
    candidates.find((c) => c.step >= target) ?? candidates[candidates.length - 1];
  return {
    src: fallback.url,
    srcSet: candidates.map((c) => `${c.url} ${c.descriptor}w`).join(', '),
  };
}

// Bucket pixel widths so we don't mint a unique CDN cache entry per device px.
function bucket(px: number): number {
  return Math.min(Math.ceil(px / 32) * 32, 4000);
}

function buildParams(t: ImageTransform): string {
  const parts: string[] = [];
  parts.push(`fit_${t.fit ?? 'cover'}`);
  if (t.width) parts.push(`w_${bucket(t.width)}`);
  if (t.height) parts.push(`h_${bucket(t.height)}`);
  parts.push(`q_${t.quality ?? 80}`);
  parts.push(`fmt_${t.format ?? 'auto'}`);
  return parts.join(',');
}

/** Rewrite the transform segment of a PUBLIC image delivery URL. No-op otherwise. */
function applyTransform(url: string, t?: ImageTransform): string {
  if (!t) return url;
  // Only public image paths are safely transformable (private URLs are signed
  // over their transforms; videos and legacy paths have no params slot).
  return url.replace(/(\/public\/img\/)([^/]+)(\/)/, (_m, pre: string, _params: string, post: string) => {
    return `${pre}${buildParams(t)}${post}`;
  });
}

// ─── URL normalization ───────────────────────────────────────────────────────

function rewriteHost(url: string): string {
  const base = isMediaPath(url) ? MEDIA_BASE_URL : API_BASE_URL;
  return url.replace(LOCALHOST_RE, base);
}

function getFullUrl(url: string | null | undefined, transform?: ImageTransform): string | null {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:')) return url;

  let absolute: string;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    absolute = rewriteHost(url);
  } else {
    const normalizedPath = url.startsWith('/') ? url : `/${url}`;
    const base = isMediaPath(normalizedPath) ? MEDIA_BASE_URL : API_BASE_URL;
    absolute = `${base}${normalizedPath}`;
  }
  return applyTransform(absolute, transform);
}

// ─── Public API ───────────────────────────────────────────────────────────────

function firstUrl(media?: Media | null): string | null {
  if (!media) return null;
  if (Array.isArray(media.url) && media.url.length > 0) return media.url[0];
  if (typeof media.url === 'string') return media.url as unknown as string;
  return null;
}

export function safeImageUrl(media?: Media | null, transform?: ImageTransform): string | null {
  return getFullUrl(firstUrl(media), transform);
}

/**
 * Host-normalize a plain URL string (e.g. a user's stored `avatar`). Like
 * `safeImageUrl` but for a raw string rather than a Media object — rewrites
 * localhost hosts to the configured media/API base so images stored as
 * absolute `http://localhost:...` URLs actually load in production.
 */
export function safeRemoteUrl(url?: string | null, transform?: ImageTransform): string | null {
  return getFullUrl(url ?? null, transform);
}

export function safeImageUrls(media?: Media | null, transform?: ImageTransform): string[] {
  if (!media || !media.url) return [];
  const urls = Array.isArray(media.url) ? media.url : [media.url];
  return urls.map((u) => getFullUrl(u, transform)).filter(Boolean) as string[];
}

export function safeCoverUrl(
  coverImage?: Media | string | null,
  transform?: ImageTransform,
): string | null {
  if (!coverImage) return null;
  if (typeof coverImage === 'string') return getFullUrl(coverImage, transform);
  if (typeof coverImage === 'object' && coverImage.url) {
    return getFullUrl(firstUrl(coverImage), transform);
  }
  return null;
}

// ─── Video media ──────────────────────────────────────────────────────────────
//
// A video promotion carries a VIDEO row (url = HLS master.m3u8) plus a
// VIDEO_THUMBNAIL image. Anything that renders an <img> must never be handed
// the VIDEO row — the browser can't decode a playlist and shows a broken image.

export function isVideoMedia(media?: Media | null): boolean {
  if (!media) return false;
  if (media.mediaType === 'VIDEO' || media.mediaType === 'FEEDBACK_VIDEO') return true;
  if (media.mimeType?.startsWith('video/')) return true;
  return /\.(m3u8|mp4|webm|mov)(\?|#|$)/i.test(firstUrl(media) ?? '');
}

/**
 * The media to show as a promotion's still artwork (cards, hero, OG image):
 * THUMBNAIL, then VIDEO_THUMBNAIL, then the first non-video item.
 */
export function pickPromotionCover(media?: Media[]): Media | undefined {
  if (!media || media.length === 0) return undefined;
  return (
    media.find((m) => m.mediaType === 'THUMBNAIL') ??
    media.find((m) => m.mediaType === 'VIDEO_THUMBNAIL') ??
    media.find((m) => !isVideoMedia(m))
  );
}

export function getPromotionImageUrl(media?: Media[], transform?: ImageTransform): string | null {
  return safeImageUrl(pickPromotionCover(media), transform);
}

// ─── Blurhash (instant LQIP placeholders) ─────────────────────────────────────
//
// The media server generates a tiny blurhash on ingest; the backend exposes it
// on Media.blurhash. The web Img component uses it to tint its shimmer
// placeholder while the real image streams in.

export function imageBlurhash(media?: Media | null): string | undefined {
  return media?.blurhash || undefined;
}

export function getPromotionBlurhash(media?: Media[]): string | undefined {
  return imageBlurhash(pickPromotionCover(media));
}
